/**
 * HeaderShareBell — in-app indicator for unread received shares.
 *
 * `external-sharing` Phase 3 Session 3.1 (2026-05-09).
 *
 * Mounts in `<Header>` to the right of the timezone clocks + branding strip.
 * Renders nothing for unauthenticated, loading, or unapproved users.
 *
 * Spec §8 originally said "between nav links and user dropdown" — neither
 * exists in the actual `<Header>` (sidebar carries nav at __root.tsx:166-167).
 * The bell sits at the right cluster instead. Do NOT add nav links to
 * `<Header>` to reconcile the spec language.
 *
 * Click → navigates to `/shares?tab=received&highlight=<latest-unread-id>`.
 * Realtime invalidations + the 5min polling fallback keep the badge fresh
 * (see `use-share-realtime.ts` mounted once at <RootLayout>).
 */
import { Bell } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { useApprovalStatus } from '@/hooks/use-approval-status';
import { useUnreadShareCount, useReceivedShares } from '@/hooks/use-shares';

const BADGE_CAP = 99;

export function HeaderShareBell() {
  const { isApproved, isLoading: approvalLoading } = useApprovalStatus();
  const { data: unread } = useUnreadShareCount();
  // Pre-fetch the received list so the click navigation can pass a `highlight`
  // param pointing at the most-recent unread share. The hook's `enabled` gate
  // matches the bell's mount gate, so this query only fires when the bell is
  // actually rendered.
  const { data: received } = useReceivedShares();
  const navigate = useNavigate();

  if (approvalLoading) return null;
  if (!isApproved) return null;

  const count = unread?.unreadCount ?? 0;
  const badgeText = count > BADGE_CAP ? `${BADGE_CAP}+` : String(count);
  const showBadge = count > 0;

  const latestUnreadId = received?.find(
    (s) => s.recipientFirstViewedAt === null
  )?.id;

  const tooltip =
    count === 0
      ? 'No unread shares'
      : count === 1
      ? '1 unread shared deal'
      : `${count} unread shared deals`;

  const handleClick = (): void => {
    void navigate({
      to: '/shares',
      search: {
        tab: 'received',
        ...(latestUnreadId ? { highlight: latestUnreadId } : {}),
      },
    });
  };

  return (
    <button
      type="button"
      data-testid="header-share-bell"
      data-unread-count={count}
      onClick={handleClick}
      title={tooltip}
      aria-label={tooltip}
      className={clsx(
        'relative flex items-center justify-center w-8 h-8 rounded-[2px]',
        'border border-panel-border bg-panel-bg',
        'text-text-muted hover:text-text-bright hover:border-accent/30',
        'transition-colors cursor-pointer shrink-0'
      )}
    >
      <Bell size={16} aria-hidden="true" />
      {showBadge && (
        <span
          data-testid="header-share-bell-badge"
          className={clsx(
            'absolute -top-1 -right-1',
            'min-w-[16px] h-4 px-1',
            'rounded-full',
            'flex items-center justify-center',
            'bg-accent text-bg',
            'text-micro font-mono font-semibold tabular-nums',
            'leading-none'
          )}
        >
          {badgeText}
        </span>
      )}
    </button>
  );
}
